<template>
  <teleport v-if="pipContainer" :to="pipContainer">
    <twilio-pip-dialer />
  </teleport>

  <!-- Call notes modal for incoming calls accepted via PiP -->
  <teleport v-if="callDialog && showCallDialog" to="#modals">
    <communication-log-call-dialog
      class="fixed z-50 shadow-lg"
      :value="callDialog"
      @close-dialog="closeCallDialog"
    />
  </teleport>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  toRaw,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";

import * as Sentry from "@sentry/vue";
import { Call, Device } from "@twilio/voice-sdk";
import { storeToRefs } from "pinia";

import {
  TwilioCallActions,
  TwilioCallDirection,
  TwilioCallEvents,
} from "@/enums/communicationProviders";
import {
  TWILIO_PIP_CLOSE,
  TWILIO_PIP_CLOSED,
  TWILIO_PIP_OPEN,
} from "@/helpers/constants/events";
import {
  leaveChannel,
  subscribeToChannelEvents,
} from "@/helpers/websockets/channels";
import { CALL_CHANNEL } from "@/helpers/websockets/common";
import { useEmitter } from "@/hooks/common";
import { useTwilio } from "@/hooks/communicationProviders";
import { useNotification } from "@/hooks/notifications";
import { CALL_REQUEST_TYPES, useCallsStore } from "@/stores/calls";

import CommunicationLogCallDialog from "@/components/communicationLogs/communication/CommunicationLogCallDialog.vue";
import TwilioPipDialer from "@/components/twilio/TwilioPiPDialer.vue";

import type { ActiveCallSession } from "@/stores/calls";

const deviceOptions = {
  closeProtection: true,
  codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
  tokenRefreshMs: 30_000,
  edge: ["umatilla", "ashburn", "dublin", "singapore", "sao-paulo"],
  maxCallSignalingTimeoutMs: 30_000,
} satisfies Device.Options;

const deviceSize = {
  width: 340,
  height: 450,
} as const;

const emitter = useEmitter();
const { t } = useI18n();
const callStore = useCallsStore();
const {
  activeCallSession,
  callDialog,
  callRequestData,
  isDialing,
  callDirection,
  isMicrophoneMuted,
  isRingtoneSilenced,
} = storeToRefs(callStore);
const { showMessage } = useNotification();
const { twilioAccessToken, fetchTwilioToken } = useTwilio();

const pipContainer = shallowRef<HTMLElement | null>(null);
const pipWindow = shallowRef<Window | null>(null);

const showCallDialog = ref(false);
const subscribedCallId = ref<string | null>(null);

const isSupported = computed(() => "documentPictureInPicture" in window);

const copyStyles = (pip: Window) => {
  document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
    pip.document.head.appendChild(node.cloneNode(true));
  });
};

const openPiP = async (options?: {
  width?: number;
  height?: number;
}): Promise<Window | null> => {
  if (!isSupported.value) {
    showMessage(t("ERRORS.TWILIO.PIP_NOT_SUPPORTED"), "error");
    return null;
  }
  if (pipWindow.value && !pipWindow.value.closed) {
    pipWindow.value.close();
  }
  try {
    // @ts-expect-error - documentPictureInPicture is not in TypeScript types yet
    const pip = await window.documentPictureInPicture.requestWindow({
      width: options?.width ?? deviceSize.width,
      height: options?.height ?? deviceSize.height,
    });
    pipWindow.value = pip;
    copyStyles(pip);
    pip.addEventListener("pagehide", () => {
      pipWindow.value = null;
      emitter.emit(TWILIO_PIP_CLOSED);
    });
    return pip;
  } catch {
    showMessage(t("ERRORS.TWILIO.FAILED_TO_OPEN_PIP"), "error");
    return null;
  }
};

const closePiP = () => {
  if (pipWindow.value && !pipWindow.value.closed) {
    pipWindow.value.close();
  }
  pipWindow.value = null;
};

const closeCallDialog = () => {
  showCallDialog.value = false;
  callDialog.value = null;
};

const handlePiPOpen = async () => {
  if (!isSupported.value) {
    showMessage(t("ERRORS.TWILIO.PIP_NOT_SUPPORTED"), "error");
    return;
  }

  const pip = await openPiP({
    width: deviceSize.width,
    height: deviceSize.height,
  });
  if (!pip) {
    showMessage(t("ERRORS.TWILIO.PIP_NOT_OPEN"), "error");
    return;
  }
  // Create a container div in the PiP window
  const container = pip.document.createElement("div");
  container.id = "twilio-pip-root";
  container.style.height = "100%";
  pip.document.body.appendChild(container);
  pip.document.body.style.margin = "0";
  pip.document.body.style.padding = "0";
  pip.document.body.style.height = "100%";
  pip.document.documentElement.style.height = "100%";

  pipContainer.value = container;
  callStore.setPiPOpen(true);

  // If there's an incoming call, notify other tabs to stop their sound
  if (callStore.hasIncomingCall) {
    callStore.notifyIncomingCallHandled();
  }
};

const handlePiPClose = () => {
  closePiP();
  onNativePiPClosed();
  emitter.emit(TWILIO_PIP_CLOSED);
};

const onNativePiPClosed = () => {
  pipContainer.value = null;
  callStore.setPiPOpen(false);
};

const setupDevice = async () => {
  if (callStore.device) {
    return;
  }
  if (!twilioAccessToken.value) {
    await fetchTwilioToken();
  }
  if (!twilioAccessToken.value) {
    return;
  }
  try {
    const device = new Device(twilioAccessToken.value, deviceOptions);
    device.audio?.incoming(false);
    device.on(TwilioCallEvents.Incoming, handleIncomingCall);
    device.on(TwilioCallEvents.TokenWillExpire, handleTokenExpiring);
    device.on(TwilioCallEvents.Error, handleError);
    await device.register();
    callStore.setDevice(device);
  } catch (error) {
    showMessage(t("ERRORS.TWILIO.DEVICE_SET_UP_ERROR"), "error");
    Sentry.captureException(error);
  }
};

const handleIncomingCall = (call: Call) => {
  // Set call state in store (this plays sound by default)
  callStore.setIncomingCall(call);

  // If PiP is open in THIS tab, we're the designated handler
  if (pipContainer.value) {
    // Notify other tabs to stop their sound
    callStore.notifyIncomingCallHandled();
  }

  call.on(TwilioCallActions.Cancel, () => {
    callStore.clearCallState();
  });
};

const handleTokenExpiring = async () => {
  await fetchTwilioToken();
  if (twilioAccessToken.value && callStore.device) {
    toRaw(callStore.device)?.updateToken(twilioAccessToken.value);
  }
};

const handleError = (error: { code: number; message: string }) => {
  showMessage(t("ERRORS.TWILIO.DEVICE_ERROR"), "error");
  Sentry.captureException(error);
};

const cleanupDevice = () => {
  const device = toRaw(callStore.device);
  if (!device) {
    return;
  }
  device.unregister();
  device.removeAllListeners();
  callStore.setDevice(null);
};

const handleCallEnded = () => {
  // Unsubscribe from call channel when call ends
  if (subscribedCallId.value) {
    leaveChannel(CALL_CHANNEL, subscribedCallId.value);
    subscribedCallId.value = null;
  }
  callStore.clearCallState();
};

const initiateOutgoingCall = async (session?: ActiveCallSession | null) => {
  const device = callStore.device;
  if (!session?.data || !device) {
    showMessage(t("ERRORS.TWILIO.FAILED_TO_INITIATE_CALL"), "error");
    return;
  }
  isDialing.value = true;
  callDirection.value = TwilioCallDirection.Outgoing;
  activeCallSession.value = session;
  try {
    const call = await toRaw(device).connect({
      params: {
        To: session.to,
        sessionId: session.data.session_id ?? "",
      },
    });
    callStore.setActiveCall(call);
    isDialing.value = false;

    // Subscribe to call channel to receive phone number updates
    if (session.data.id) {
      subscribedCallId.value = session.data.id.toString();
      subscribeToChannelEvents(CALL_CHANNEL, subscribedCallId.value);
    }

    call.on(TwilioCallActions.Disconnect, handleCallEnded);
    call.on(TwilioCallActions.Cancel, handleCallEnded);
    call.on(TwilioCallActions.Mute, (isMuted: boolean) => {
      isMicrophoneMuted.value = isMuted;
    });
  } catch (error) {
    showMessage(t("ERRORS.TWILIO.FAILED_TO_INITIATE_CALL"), "error");
    callStore.clearCallState();
  }
};

// Watch for outgoing call requests from this tab
// Only initiate if: PiP is open HERE, or PiP is not open anywhere (this tab handles it)
watch(activeCallSession, (session) => {
  if (!session) {
    return;
  }
  // If PiP is open in another tab (isPiPOpen is true but pipContainer is null),
  // let that tab handle it via the broadcast
  const pipOpenInAnotherTab = callStore.isPiPOpen && !pipContainer.value;
  if (pipOpenInAnotherTab) {
    return;
  }
  initiateOutgoingCall(session);
});

// Watch for call requests from OTHER tabs via BroadcastChannel
watch(
  callRequestData,
  (payload) => {
    if (!payload) {
      return;
    }
    // Another tab is handling the incoming call - stop our sound
    if (payload.type === CALL_REQUEST_TYPES.INCOMING_CALL_HANDLED) {
      callStore.stopIncomingSound();
      return;
    }
    // Another tab silenced/muted the call - stop our sound too
    if (payload.type === CALL_REQUEST_TYPES.CALL_SILENCED) {
      callStore.stopIncomingSound();
      isRingtoneSilenced.value = true;
      return;
    }
    // Another tab ended/rejected the call - clear our state
    if (payload.type === CALL_REQUEST_TYPES.CALL_ENDED) {
      callStore.clearCallState();
      return;
    }
    // Phone number update from another tab - sync to our stores
    if (
      payload.type === CALL_REQUEST_TYPES.PHONE_NUMBER_UPDATE &&
      payload.phoneNumber
    ) {
      if (activeCallSession.value) {
        activeCallSession.value = {
          ...activeCallSession.value,
          data: {
            ...activeCallSession.value.data,
            from: payload.phoneNumber,
          } as typeof activeCallSession.value.data,
        };
      }
      return;
    }
    // Only handle outgoing call broadcasts if PiP is open in THIS tab
    if (!pipContainer.value) {
      return;
    }
    if (payload.type === CALL_REQUEST_TYPES.INITIATE_CALL && payload.session) {
      initiateOutgoingCall(payload.session);
    }
  },
  { immediate: false }
);

watch(callDialog, (newValue) => {
  if (newValue) {
    showCallDialog.value = true;
  }
});

// Update device token when it changes (e.g., manual refresh via RefreshVoiceToken)
watch(twilioAccessToken, (newToken) => {
  if (newToken && callStore.device) {
    toRaw(callStore.device)?.updateToken(newToken);
  }
});

onMounted(async () => {
  callStore.setPiPSupported(isSupported.value);
  emitter.on(TWILIO_PIP_OPEN, handlePiPOpen);
  emitter.on(TWILIO_PIP_CLOSE, handlePiPClose);
  emitter.on(TWILIO_PIP_CLOSED, onNativePiPClosed);
  await setupDevice();
});

onBeforeUnmount(closePiP);

onUnmounted(() => {
  emitter.off(TWILIO_PIP_OPEN, handlePiPOpen);
  emitter.off(TWILIO_PIP_CLOSE, handlePiPClose);
  emitter.off(TWILIO_PIP_CLOSED, onNativePiPClosed);
  handlePiPClose();
  cleanupDevice();
});
</script>
